import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { Big } from 'big.js';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  options: any = {};

  protected readonly decimalPlaces = 4;

  /**
   * Convertit une valeur du formulaire en décimal exact. Une valeur absente ou
   * vide vaut zéro, le template déclenchant les calculs avant toute saisie du
   * client ; une saisie qui n'est pas un nombre ne donne aucun décimal.
   */
  protected toBig(value: any): Big | null {
    if (value === null || value === undefined || value === '') {
      return new Big(0);
    }
    try {
      return new Big(typeof value === 'string' ? value.trim() : value);
    } catch {
      return null;
    }
  }

  /** Décimal exact d'une valeur du formulaire, une saisie non numérique valant zéro. */
  protected toBigOrZero(value: any): Big {
    return this.toBig(value) ?? new Big(0);
  }

  /**
   * Rend une valeur sous forme de chaîne, arrondie au plus proche sur le
   * nombre de décimales demandé et sans zéro de fin superflu. Une valeur non
   * numérique reste « NaN », qui ne satisfait pas le format attendu du nombre
   * de parts.
   */
  protected format(value: any, decimalPlaces = this.decimalPlaces): string {
    const decimal = this.toBig(value);
    return decimal === null
      ? 'NaN'
      : decimal.round(decimalPlaces, Big.roundHalfUp).toString();
  }

  generate(entity: any, options) {
    this.options = options;
    let scpis: UntypedFormGroup[] = [];
    const products = entity;

    const { scpiOwnershipType, investmentDurationViager } =
      this.options?.pledgeParameters || {};
    const isDismemberment =
      scpiOwnershipType &&
      ['USUFRUIT', 'NU_PROPRIO'].includes(scpiOwnershipType);

    if (!!products)
      for (let scpi of products)
        scpis.push(
          this.formBuilder.group(this.createModel(scpi, isDismemberment)),
        );

    let group = this.formBuilder.group({
      // Souscription en viager : paramètre de la souscription, pas du produit.
      // Le back ne le porte que sur `pledge.parameters`.
      parameters: this.formBuilder.group({
        investmentDurationViager: [
          investmentDurationViager ?? false,
          isDismemberment ? Validators.required : Validators.nullValidator,
        ],
      }),
      scpis: this.formBuilder.array(scpis, Validators.nullValidator),
    });

    this.form = group;

    return group;
  }

  public createModel(scpi, isDismemberment) {
    const mostRecentPrice = scpi?.priceHistory?.length
      ? scpi?.priceHistory?.reduce((prev, current) => {
          return new Date(prev.date) > new Date(current.date) ? prev : current;
        })
      : null;

    let currentPartPrice = mostRecentPrice
      ? mostRecentPrice.value
      : scpi.partPrice;
    let bareOwnershipKeys = scpi.dismembermentKeys.bareOwnership;
    let usufructKeys = scpi.dismembermentKeys.usuFruict;
    const allowDecimal = !!scpi?.scpiPartDivisible;

    let model = {
      productId: [scpi ? scpi.id : null],
      productName: [scpi ? scpi.name : null],
      parameters: this.formBuilder.group({
        scpiPartDivisible: [scpi?.scpiPartDivisible || false],
        allocationPercentage: [scpi ? scpi.allocationPercentage : null],
        dismembermentKey: [
          scpi && isDismemberment ? scpi.dismembermentKey : null,
        ],
        dismembermentDuration: [
          scpi && isDismemberment ? scpi.dismembermentDuration : null,
          isDismemberment ? Validators.required : Validators.nullValidator,
        ],
        dismembermentDistributionKey: [
          scpi?.dismembermentDistributionKey && isDismemberment
            ? this.format(
                this.toBigOrZero(scpi.dismembermentDistributionKey).times(100),
              )
            : null,
          isDismemberment ? Validators.required : Validators.nullValidator,
        ],
        scpiDismembermentPartPrice: [
          scpi && isDismemberment ? scpi.scpiDismembermentPartPrice : null,
        ],
        dismembermentFullPropertyAmount: [
          scpi && isDismemberment ? scpi.dismembermentFullPropertyAmount : null,
        ],
        totalInvestedAmount: [
          this.options ? this.options.investedAmount : null,
        ],
        shareCount: [
          scpi && scpi.allocationPercentage && currentPartPrice
            ? Math.floor(
                (scpi.allocationPercentage * this.options.investedAmount) /
                  currentPartPrice,
              )
            : null,
        ],
        scpiPartPrice: [scpi ? currentPartPrice : null],
        //
        type: [
          scpi?.scpiNbPart || scpi?.nbShare || scpi?.slightlyAmount ? 'B' : 'A',
        ],
        scpiNbPart: [
          scpi?.scpiNbPart || scpi?.nbShare || scpi?.slightlyAmount || null,
          [
            Validators.required,
            Validators.pattern(
              allowDecimal ? '[0-9]+(\\.[0-9]+)?' : '[0-9]+',
            ),
          ],
        ],
        amount: [scpi?.amount || null],
        slightlyAmount: [scpi?.slightlyAmount || null],
        scpiInvestmentPrice: [scpi?.scpiInvestmentPrice || null],
        bareOwnershipKeys: [bareOwnershipKeys || null],
        usufructKeys: [usufructKeys || null],
      }),
    };
    return model;
  }
  get scpis() {
    return this.form.get('scpis') as UntypedFormArray;
  }
  getSlightlyAmountValue(id) {
    let scpi = this.scpis.controls.find(scpi => scpi.value.id === id);
    return scpi.value.parameters.slightlyAmount;
  }

  extract() {
    const formValue = _.cloneDeep(this.form.value.scpis);
    const scpis = formValue?.map(scpi => {
      const investmentPrice = this.getInvestmentPrice(scpi);

      return {
        ...scpi,
        productId: scpi.productId,
        productName: scpi.productName,
        parameters: {
          ...scpi.parameters,
          scpiNbPart:
            scpi?.parameters?.scpiNbPart || scpi?.parameters?.slightlyAmount,
          scpiPartPrice: scpi?.parameters?.scpiPartPrice,
          scpiInvestmentPrice:
            scpi?.parameters?.scpiInvestmentPrice || investmentPrice,
          scpiOwnershipType: scpi?.parameters?.scpiOwnershipType,
          dismembermentDuration: scpi?.parameters?.dismembermentDuration,
          dismembermentDistributionKey: scpi?.parameters
            ?.dismembermentDistributionKey
            ? this.toBigOrZero(scpi?.parameters?.dismembermentDistributionKey)
                .div(100)
                .toNumber()
            : undefined,
          scpiDismembermentPartPrice:
            scpi?.parameters?.scpiDismembermentPartPrice,
          dismembermentFullPropertyAmount:
            scpi?.parameters?.dismembermentFullPropertyAmount,
        },
      };
    });

    return {
      products: scpis,
      parameters: {
        investmentDurationViager:
          this.form.value?.parameters?.investmentDurationViager ?? false,
      },
    };
  }

  private getInvestmentPrice(scpi: any) {
    const partPrice = this.toBigOrZero(scpi?.parameters.scpiPartPrice);
    const nbPart = this.toBigOrZero(
      scpi?.parameters.scpiNbPart || scpi?.parameters.slightlyAmount,
    );
    if (partPrice.eq(0) || nbPart.eq(0)) {
      return null;
    }

    const key = scpi?.parameters?.dismembermentDistributionKey;
    const investmentPrice = key
      ? this.toBigOrZero(key).div(100).times(partPrice).times(nbPart)
      : nbPart.times(partPrice);

    return Number(this.format(investmentPrice));
  }
}
